// React Query hooks for the WB products admin surface (Phase C3 Group 4).
//
// Pairs with /api/v1/wb/products/* — list (paginated + filterable) /
// detail / create / patch / delete. `enc_id` is the encrypted scalar id
// (snake_case throughout the WB module). DTOs mirror
// server/src/modules/wb/products/types.ts exactly.
//
// Also exports `useWbVatRates` — the VAT rate dropdown reads from a
// separate global+tenant-merged endpoint (/wb/vat-rates) that is NOT
// paginated; the response is a plain `{ vat_rates: [...] }`. VAT rates
// change rarely, so 60s staleTime keeps the dropdown snappy.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';

// -- Client-side types (mirror WbProductDto on the server) ----------------

export type ProductMode = 'weight' | 'unit';
export type UnitOfMeasure = 'tonne' | 'kg' | 'each' | 'm3';
export type DirectionDefault = 'inwards' | 'outwards' | 'both';

export interface ProductVatRate {
  readonly enc_id: string;
  readonly name: string;
  readonly code: string;
  readonly percentage: number;
}

/** Minimal group reference embedded on a product (migration 169). */
export interface ProductGroupRef {
  readonly enc_id: string;
  readonly name: string;
  readonly code: string;
}

export interface WbProduct {
  readonly enc_id: string;
  readonly name: string;
  readonly code: string;
  readonly group: ProductGroupRef | null;
  readonly description: string | null;
  readonly mode: ProductMode;
  readonly unit_of_measure: UnitOfMeasure;
  readonly default_general_public_price: number;
  readonly vat_rate: ProductVatRate | null;
  readonly ewc_code: string | null;
  readonly is_hazardous: boolean;
  readonly direction_default: DirectionDefault;
  readonly stock_tracked: boolean;
  readonly stock_threshold_low: number | null;
  readonly stock_threshold_high: number | null;
  readonly stock_threshold_unit: string | null;
  readonly is_active: boolean;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface Pagination {
  readonly page: number;
  readonly per_page: number;
  readonly total: number;
  readonly total_pages: number;
}

export interface CreateProductInput {
  name: string;
  code: string;
  /** enc_id of the assigned group, or null to clear. */
  group_id?: string | null;
  description?: string | null;
  mode: ProductMode;
  unit_of_measure: UnitOfMeasure;
  default_general_public_price: number;
  vat_rate_id?: string | null;
  ewc_code?: string | null;
  is_hazardous?: boolean;
  direction_default?: DirectionDefault;
  stock_tracked?: boolean;
  stock_threshold_low?: number | null;
  stock_threshold_high?: number | null;
  stock_threshold_unit?: UnitOfMeasure | null;
  is_active?: boolean;
}

export type UpdateProductInput = Partial<CreateProductInput>;

export interface ListProductsParams {
  page?: number;
  per_page?: number;
  name?: string;
  mode?: ProductMode;
  direction_default?: DirectionDefault;
  stock_tracked?: boolean;
  is_active?: boolean;
  /** enc_id of a group to filter by. */
  group_id?: string;
}

interface ListProductsResponse {
  products: WbProduct[];
  pagination: Pagination;
}

export interface WbVatRate {
  readonly enc_id: string;
  readonly name: string;
  readonly code: string;
  readonly percentage: number;
  readonly is_exempt: boolean;
  readonly is_default: boolean;
  readonly is_active: boolean;
  readonly is_global: boolean;
  readonly created_at: string;
  readonly updated_at: string;
}

interface ListVatRatesResponse {
  vat_rates: WbVatRate[];
}

// -- Helpers ----------------------------------------------------------------

function buildQueryString(params: ListProductsParams): string {
  const sp = new URLSearchParams();
  if (params.page !== undefined) sp.set('page', String(params.page));
  if (params.per_page !== undefined) sp.set('per_page', String(params.per_page));
  if (params.name !== undefined && params.name !== '') sp.set('name', params.name);
  if (params.mode !== undefined) sp.set('mode', params.mode);
  if (params.direction_default !== undefined) {
    sp.set('direction_default', params.direction_default);
  }
  if (params.stock_tracked !== undefined) {
    sp.set('stock_tracked', String(params.stock_tracked));
  }
  if (params.is_active !== undefined) sp.set('is_active', String(params.is_active));
  if (params.group_id !== undefined && params.group_id !== '') {
    sp.set('group_id', params.group_id);
  }
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

// -- Fetchers -----------------------------------------------------------
//
// Exported so callers outside a component (e.g. KioskRoot's login
// prefetch — S63G) can warm the exact same cache entry the hooks below
// populate, instead of re-implementing the URL construction. Any change
// to the query shape here is automatically picked up by every caller.

export async function fetchWbProducts(
  params: ListProductsParams = {},
): Promise<ListProductsResponse> {
  const qs = buildQueryString(params);
  const res = await apiClient.get<ListProductsResponse>(
    `${endpoints.wb.products}${qs}`,
  );
  return res.data;
}

export async function fetchWbVatRates(): Promise<ListVatRatesResponse> {
  const res = await apiClient.get<ListVatRatesResponse>(endpoints.wb.vatRates);
  return res.data;
}

// -- Queries ----------------------------------------------------------------

export function useWbProducts(params: ListProductsParams = {}) {
  return useQuery<ListProductsResponse>({
    queryKey: queryKeys.wb.products(params as Record<string, unknown>),
    queryFn: () => fetchWbProducts(params),
    staleTime: 15 * 60 * 1000, // Server caches for 30min; 15min client staleTime is safe
  });
}

export function useWbProduct(encId: string | undefined) {
  const enabled = !!encId && encId !== 'new';
  return useQuery<{ product: WbProduct }>({
    queryKey: queryKeys.wb.product(encId ?? ''),
    queryFn: async () => {
      if (!encId) throw new Error('useWbProduct called without enc_id');
      const res = await apiClient.get<{ product: WbProduct }>(
        endpoints.wb.product(encId),
      );
      return res.data;
    },
    enabled,
    staleTime: 15 * 60 * 1000, // Server caches for 30min; 15min client staleTime is safe
  });
}

export function useWbVatRates() {
  return useQuery<ListVatRatesResponse>({
    queryKey: queryKeys.wb.vatRates,
    queryFn: fetchWbVatRates,
    staleTime: 15 * 60 * 1000, // Server caches for 30min; 15min client staleTime is safe
  });
}

// -- Mutations --------------------------------------------------------------

export function useCreateProduct() {
  const qc = useQueryClient();
  return useMutation<{ product: WbProduct }, Error, CreateProductInput>({
    mutationFn: async (body) => {
      const res = await apiClient.post<{ product: WbProduct }, CreateProductInput>(
        endpoints.wb.products,
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.wb.products() });
    },
  });
}

export function useUpdateProduct(encId: string) {
  const qc = useQueryClient();
  return useMutation<{ product: WbProduct }, Error, UpdateProductInput>({
    mutationFn: async (body) => {
      const res = await apiClient.patch<{ product: WbProduct }, UpdateProductInput>(
        endpoints.wb.product(encId),
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.wb.products() });
      void qc.invalidateQueries({ queryKey: queryKeys.wb.product(encId) });
    },
  });
}

export function useDeleteProduct(encId: string) {
  const qc = useQueryClient();
  return useMutation<{ deleted: true }, Error, void>({
    mutationFn: async () => {
      const res = await apiClient.delete<{ deleted: true }>(
        endpoints.wb.product(encId),
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.wb.products() });
      void qc.invalidateQueries({ queryKey: queryKeys.wb.product(encId) });
    },
  });
}
